import { saveAs } from "file-saver";
import JSZip from "jszip";
import { OBJExporter } from "three/examples/jsm/exporters/OBJExporter";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";

import { DriveFileFragment } from "@/features/drive/queries.graphql";

import { fetchWithRetry } from "./fetch-retry";
import {
  type MeshFormat,
  meshFormatFileName,
  meshFormatUrl,
} from "./meshFormats";

type DownloadableFile = { name: string; url: string };

type DownloadOptions = {
  zipName?: string;
  maxConcurrentFiles?: number;
};

/**
 * Downloads a single file from a URL as a blob.
 * Falls back to anchor-based download if fetch fails.
 */
const downloadSingleFromURL = async (file: DownloadableFile) => {
  const { name, url } = file;
  try {
    const response = await fetchWithRetry(url);
    const blob = await response.blob();
    saveAs(blob, name);
  } catch (error) {
    console.error("Blob download failed:", error);
  }
};

/**
 * Downloads multiple files from URLs and bundles them into a ZIP.
 */
const downloadMultipleFromURL = async (
  files: DownloadableFile[],
  { zipName = "files.zip", maxConcurrentFiles = 8 }: DownloadOptions = {},
) => {
  const zip = new JSZip();

  const processBatch = async (
    batchFiles: DownloadableFile[],
    startIndex: number,
  ) => {
    await Promise.all(
      batchFiles.map(async (file, index) => {
        const fetchedFile = await fetchWithRetry(file.url);
        const fetchedFileBlob = await fetchedFile.blob();

        // Have to add index suffix in case of name conflicts
        zip.file(
          file.name.replace(".", `_${startIndex + index + 1}.`),
          fetchedFileBlob,
        );
      }),
    );
  };

  // Loop through the files in batches
  for (let i = 0; i < files.length; i += maxConcurrentFiles) {
    const batchFiles = files.slice(i, i + maxConcurrentFiles);
    await processBatch(batchFiles, i);
  }

  // Generate the ZIP and trigger download
  const zipBlob = await zip.generateAsync({ type: "blob" });
  saveAs(zipBlob, zipName);
};

/**
 * Downloads one or more files from URLs.
 * - For a single file: downloads directly as a blob
 * - For multiple files: automatically bundles them into a ZIP
 *
 * This approach handles cross-origin resources properly, unlike directly
 * passing a URL to saveAs which may fail or just open the URL in a new tab.
 */
export const downloadFromURL = (
  files: DownloadableFile[],
  options: DownloadOptions = {
    maxConcurrentFiles: 8,
    zipName: "files.zip",
  },
) => {
  if (files.length === 0) return;

  if (files.length === 1) {
    return downloadSingleFromURL(files[0]);
  }

  return downloadMultipleFromURL(files, {
    maxConcurrentFiles: options.maxConcurrentFiles,
    zipName: options.zipName,
  });
};

/**
 * Downloads a 3D file in one of its stored interchange formats (FBX, OBJ).
 * Returns false when the file has no export in that format.
 */
export const downloadMeshFormat = async (
  file: DriveFileFragment,
  format: MeshFormat,
) => {
  const url = meshFormatUrl(file, format);
  if (!url || !file.name) return false;
  await downloadSingleFromURL({
    url,
    name: meshFormatFileName(file.name, format),
  });
  return true;
};

/**
 * Downloads a 3D file as OBJ, preferring the OBJ the generating model exported.
 * Falls back to converting the GLB in the browser for files stored before that
 * export was kept — that conversion drops materials, animations and quad topology,
 * so it is only a fallback.
 */
export const downloadAsOBJ = async (file: DriveFileFragment) => {
  if (await downloadMeshFormat(file, "obj")) return;
  await downloadGLBAsOBJ(file);
};

/**
 * Converts a GLB/GLTF 3D model file to OBJ format and downloads it.
 * Uses Three.js GLTFLoader to parse the binary GLB file, then exports
 * the scene geometry to the OBJ text format using OBJExporter.
 * Note: OBJ format does not preserve materials, animations, or other
 * advanced features from the original GLB file.
 */
export const downloadGLBAsOBJ = async (file: DriveFileFragment) => {
  const loader = new GLTFLoader();
  const exporter = new OBJExporter();

  const { url, name } = file;
  if (!url || !name) {
    throw new Error("File URL and name are required for GLB to OBJ conversion");
  }

  // Load the GLB file
  const gltf = (await new Promise((resolve, reject) => {
    loader.load(url, resolve, undefined, reject);
  })) as any;

  // Extract the scene
  const scene = gltf.scene;

  // Convert to OBJ format
  const objString = exporter.parse(scene);

  // Remove the original file extension if present
  const baseFileName = name.includes(".")
    ? name.substring(0, name.lastIndexOf("."))
    : name;

  // Initiate download
  const blob = new Blob([objString], { type: "text/plain" });
  saveAs(blob, `${baseFileName}.obj`);
};
